import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Badge } from '../../atoms';
import { useLawEdition } from '../useLawEdition';
import { type Matter, statusVariant } from '../pages/MattersIndex';

const muted = { color: 'var(--color-ink-3)' } as const;

/**
 * The matter(s) this property's appeals roll up into — read-only, from the master-chart
 * (same query key as the other law surfaces → one deduped fetch). A property usually rolls
 * into one matter or none; more than one is possible across tax years.
 */
export function usePropertyMatters(propertyId: string) {
  const { mattersOn } = useLawEdition();
  const q = useQuery({
    queryKey: ['law-master-chart'],
    queryFn: ({ signal }) => api.get<{ matters: Matter[] }>('/api/law/master-chart', signal),
    enabled: mattersOn && !!propertyId,
    retry: false,
  });
  const matters = (q.data?.matters ?? []).filter((m) =>
    (m.properties ?? []).some((p) => p.property_id === propertyId));
  return { mattersOn, matters, isLoading: q.isLoading };
}

/** The read-only "Matter" indicator tab on the property detail page — links to the matter
 *  its appeals are grouped under, or says plainly when they aren't grouped. */
export function PropertyMatterTab({ propertyId }: { propertyId: string }) {
  const { mattersOn, matters, isLoading } = usePropertyMatters(propertyId);

  if (!mattersOn) return null;
  if (isLoading) return <div style={{ padding: 'var(--sp-4)', ...muted }}>Loading…</div>;
  if (matters.length === 0) {
    return <div style={{ padding: 'var(--sp-4)', ...muted }}>This property’s appeals aren’t grouped into a matter.</div>;
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
      {matters.map((m) => (
        <div key={m.id} style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--r, 10px)', padding: 'var(--sp-4)', background: 'var(--color-surface, #fff)', display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', flexWrap: 'wrap' }}>
          <Link to={`/law/matters/${m.id}`} style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--brand-primary)', textDecoration: 'none' }}>{m.name}</Link>
          {m.case_no && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', ...muted }}>{m.case_no}</span>}
          <span style={{ ...muted, fontSize: 'var(--fs-sm, 13px)' }}>{m.property_count} propert{m.property_count === 1 ? 'y' : 'ies'}</span>
          <span style={{ marginLeft: 'auto' }}><Badge variant={statusVariant(m.status)}>{m.status || '—'}</Badge></span>
        </div>
      ))}
    </div>
  );
}
